'use client';

import { getReferralId } from '@/utils/referral';
import { useLingui } from '@lingui/react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { Trans } from '@lingui/macro';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import Image from 'next/image';
import { StyledRadio } from '@/entitlements/components/upsell/styled-radio';
import { ReferralQuote } from './referral-quote';
import ReferralAppBenefits from './referral-app-benefits';

import { GrowthDayLogo } from '@/assets/logos/growthday';
import melProfile from '@/assets/images/referral/mel.png';
import edProfile from '@/assets/images/referral/ed.png';
import brendonProfile from '@/assets/images/referral/brendon.png';
import guaranteeBadge from '@/assets/images/referral/badge-30day.png';

interface ReferralPageProps {
  includeFreeTrial?: boolean;
}

export default function ReferralLandingPage({
  includeFreeTrial
}: ReferralPageProps) {
  const refId = getReferralId();
  const { i18n } = useLingui();
  const contentRef = useRef<HTMLDivElement>(null);

  const parentId = includeFreeTrial
    ? 'Referral Page With Trial'
    : 'Referral Page';

  const monthlyLink = includeFreeTrial
    ? '/campaign/monthly-trial?viewMode=small'
    : '/campaign/monthly?viewMode=small';
  const annualLink = includeFreeTrial
    ? '/campaign/annual-trial?viewMode=small'
    : '/campaign/annual?viewMode=small';

  const [selectedOffer, setSelectedOffer] = useState('monthly');

  const ctaLink = selectedOffer === 'monthly' ? monthlyLink : annualLink;

  useEffect(() => {
    const flowName = includeFreeTrial
      ? 'Referral Flow Entry'
      : 'Referral Trial Flow Entry';
    rudderanalytics.track(flowName, {
      element_id: 'standalone-referral-flow',
      parent_id: parentId,
      context: {
        refId,
        includeFreeTrial
      }
    });
  }, [refId, parentId, includeFreeTrial]);

  const { ref: ctaRef, isIntersecting: isCTAIntersecting } =
    useIntersectionObserver({ freezeOnceVisible: true });
  const { ref: mobileCTARef, isIntersecting: isMobileCTAIntersecting } =
    useIntersectionObserver({ freezeOnceVisible: true });

  useEffect(() => {
    if (isCTAIntersecting) {
      rudderanalytics.track('Element Visible', {
        element_id: 'referral-cta',
        parent_id: parentId,
        type: 'BUTTON',
        context: {
          refId,
          includeFreeTrial
        }
      });
    }
  }, [isCTAIntersecting, refId, parentId, includeFreeTrial]);

  useEffect(() => {
    if (isMobileCTAIntersecting) {
      rudderanalytics.track('Element Visible', {
        element_id: 'referral-mobile-cta',
        parent_id: parentId,
        type: 'BUTTON',
        context: {
          refId,
          includeFreeTrial
        }
      });
    }
  }, [isMobileCTAIntersecting, refId, parentId, includeFreeTrial]);

  const onAnnualClicked = (event: MouseEvent<HTMLButtonElement>) => {
    rudderanalytics.track('Click', {
      element_id: 'referral-cta-annual',
      type: 'BUTTON',
      parent_id: parentId,
      context: {
        refId,
        includeFreeTrial
      }
    });
  };

  const onMonthlyClicked = (event: MouseEvent<HTMLButtonElement>) => {
    rudderanalytics.track('Click', {
      element_id: 'referral-cta-monthly',
      type: 'BUTTON',
      parent_id: parentId,
      context: {
        refId,
        includeFreeTrial
      }
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      ref={contentRef}
      sx={{
        width: '100%',
        pb: { xs: '116px', md: 0 }
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        sx={{
          pt: '24px',
          px: '12px'
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          gap="20px"
          flexWrap="wrap"
          justifyContent="center"
        >
          <Card
            sx={{
              maxWidth: '782px'
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 }
              }}
            >
              <GrowthDayLogo />
              <Typography
                variant="body1"
                fontSize="26px"
                lineHeight="34px"
                fontWeight={600}
                textAlign="center"
              >
                <Trans id="referral.accelerate-your-growth">
                  Accelerate your personal growth and success
                </Trans>
              </Typography>
              <Typography
                variant="body2"
                fontSize="16px"
                lineHeight="20px"
                textAlign="center"
              >
                <Trans id="referral.one-app-to-transform">
                  Finally - one app to transform your life! GrowthDay gives you
                  the daily training and checklist to stay focused, consistent
                  and inspired as you achieve your goals.
                </Trans>
              </Typography>

              <Stack direction="row" alignItems="center">
                <StarIcon
                  htmlColor="var(--mui-palette-secondary-main)"
                  style={{ fontSize: '16px', marginLeft: 0 }}
                />
                <StarIcon
                  htmlColor="var(--mui-palette-secondary-main)"
                  style={{ fontSize: '16px', marginLeft: 0 }}
                />
                <StarIcon
                  htmlColor="var(--mui-palette-secondary-main)"
                  style={{ fontSize: '16px', marginLeft: 0 }}
                />
                <StarIcon
                  htmlColor="var(--mui-palette-secondary-main)"
                  style={{ fontSize: '16px', marginLeft: 0 }}
                />
                <StarIcon
                  htmlColor="var(--mui-palette-secondary-main)"
                  style={{ fontSize: '16px', marginLeft: 0 }}
                />
                <Typography fontSize="14px">
                  <Trans id="referral.review-rating">4.7 (1,919 Reviews)</Trans>
                </Typography>
              </Stack>

              <ReferralQuote imgSrc={melProfile.src}>
                <Trans id="referral.mel-quote">
                  &quot;GrowthDay is the place where I come to grow, get
                  inspired, and get the tools that I need to bridge the gap
                  between where I am and where I want to go. My growth is a
                  daily commitment! &quot; - Mel Robbins
                </Trans>
              </ReferralQuote>
              <ReferralQuote imgSrc={edProfile.src}>
                <Trans id="referral.ed-quote">
                  &quot;This is like the avengers of personal growth, all here
                  to help you become more.&quot; - Ed Mylett
                </Trans>
              </ReferralQuote>
              <ReferralQuote imgSrc={brendonProfile.src}>
                <Trans id="referral.brendon-quote">
                  &quot;GrowthDay has the daily inspiration and tools we all
                  need to achieve greater success, starting now.&quot; - Brendon
                  Burchard
                </Trans>
              </ReferralQuote>

              <ReferralAppBenefits />

              <Stack
                display="flex"
                width="100%"
                direction={{ xs: 'column', md: 'row' }}
              >
                <StyledRadio
                  checked={selectedOffer === 'monthly'}
                  value={'monthly'}
                  onChange={() => setSelectedOffer('monthly')}
                  name={'monthly'}
                  key={'monthly'}
                  label={
                    <Typography
                      fontSize="20px"
                      fontWeight={600}
                      lineHeight="28px"
                    >
                      <Trans id="referral.monthly-label">Monthly</Trans>
                    </Typography>
                  }
                  subLabel={
                    <Typography fontSize="18px" lineHeight="26px">
                      <>
                        {includeFreeTrial && (
                          <>
                            <Trans id="referral.monthly-trial-label">
                              Try free for 7 days then,
                            </Trans>
                            <br />
                          </>
                        )}
                        <Trans id="referral.monthly-sub-label">
                          $49/month. Cancel Anytime
                        </Trans>
                      </>
                    </Typography>
                  }
                />
                <StyledRadio
                  checked={selectedOffer === 'annual'}
                  value={'annual'}
                  onChange={() => setSelectedOffer('annual')}
                  name={'annual'}
                  key={'annual'}
                  label={
                    <Stack direction="row" alignItems="center">
                      <Typography
                        fontSize="20px"
                        fontWeight={600}
                        lineHeight="28px"
                      >
                        <Trans id="referral.annual-label">Annual</Trans>
                      </Typography>
                      <Chip
                        size="medium"
                        label={
                          <Typography
                            fontWeight={600}
                            fontSize="15px"
                            lineHeight="20px"
                          >
                            <Trans id="referral.best-value">Best Value</Trans>
                          </Typography>
                        }
                        sx={{
                          color: 'white',
                          background:
                            'linear-gradient(87.99deg, #FB6E00 2.44%, #FF8A14 100%)'
                        }}
                      />
                      <Typography
                        fontSize="18px"
                        lineHeight="26px"
                        color="text.secondary"
                      >
                        <Trans id="referral.annual-savings">Save $89</Trans>
                      </Typography>
                    </Stack>
                  }
                  subLabel={
                    <Typography fontSize="18px" lineHeight="26px">
                      <>
                        {includeFreeTrial && (
                          <>
                            <Trans id="referral.trial-label">
                              Try free for 7 days then,
                            </Trans>
                            <br />
                          </>
                        )}
                        <Trans id="referral.annual-sub-label">
                          $499/year. Cancel Anytime
                        </Trans>
                      </>
                    </Typography>
                  }
                />
              </Stack>
              <Button ref={ctaRef} size="large" fullWidth href={ctaLink}>
                <Trans id="referral.start-my-transformation">
                  Start My Transformation!
                </Trans>
              </Button>
              {includeFreeTrial && (
                <>
                  <Typography
                    fontWeight="500"
                    fontSize="14px"
                    lineHeight="18px"
                  >
                    <Trans id="referral.money-back-guarantee">
                      30-Day Money Back Guarantee
                    </Trans>
                  </Typography>
                  <Image
                    width="100"
                    height="100"
                    src={guaranteeBadge.src}
                    alt=""
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Box>
        <Stack
          direction="column"
          alignItems="center"
          sx={{ py: '20px' }}
          gap="20px"
        >
          <Box textAlign="center">
            <Typography
              variant="body2"
              textTransform="uppercase"
              fontWeight={600}
            >
              <Trans id="referral.every-day">
                Every Day can be a Growthday!
              </Trans>
            </Typography>
            <Typography variant="body2" fontWeight={400}>
              <Trans id="referral.need-support">Need support? Email</Trans>{' '}
              <Link href="mailto:support@growthday.com" color="secondary">
                support@growthday.com
              </Link>
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="body2" fontSize="10px" lineHeight="12px">
              <Trans id="referral.copyright">
                Copyright 2018-2025 GrowthDay. All Rights Reserved.
              </Trans>
            </Typography>
            <Typography variant="body2" fontSize="10px" lineHeight="12px">
              <Link
                target="_blank"
                href="https://www.growthday.com/privacy"
                aria-label={i18n.t('sign-up.aria.privacy-policy')}
                color="secondary"
              >
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link
                target="_blank"
                href="https://www.growthday.com/terms"
                aria-label={i18n.t('sign-up.aria.terms-of-use')}
                color="secondary"
              >
                Terms of Use
              </Link>
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <Stack
        direction="column"
        position="fixed"
        display={{ xs: 'flex', md: 'none' }}
        bottom={0}
        width="100%"
        alignItems="center"
        sx={{
          background: 'linear-gradient(to right, #FF8914, #FA5F05)',
          p: '24px',
          pt: '20px'
        }}
      >
        <Typography color="white" fontSize="18px" lineHeight="27px">
          {includeFreeTrial ? (
            <Trans id="referral.start-trial">Start Your Trial Now!</Trans>
          ) : (
            <Trans id="referral.sign-up-now">Sign Up Now</Trans>
          )}
        </Typography>
        <Stack direction="row" width="100%" ref={mobileCTARef}>
          <Button
            variant="neutral"
            href={monthlyLink}
            aria-label={i18n.t('referral.monthly')}
            onClick={onMonthlyClicked}
            sx={{
              fontWeight: 600,
              flex: 1,
              background: 'white',
              color: 'secondary.main',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.8)',
                color: 'secondary.main'
              }
            }}
          >
            <Trans id="referral.monthly">Monthly</Trans>
          </Button>
          <Button
            variant="neutral"
            href={annualLink}
            aria-label={i18n.t('referral.annual')}
            onClick={onAnnualClicked}
            sx={{
              fontWeight: 600,
              flex: 1,
              background: 'white',
              color: 'secondary.main',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.8)',
                color: 'secondary.main'
              }
            }}
          >
            <Trans id="referral.annual">Annual</Trans>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
